import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { Dialog } from "../../layout";
import ViewProd from "../sells/ViewProd";
import ReportPrintButton from "../reports/ReportPrintButton";

import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { AppContext } from "../../App";
import { api, endpoints } from "../../api/actions/api";
import { formatCurrency } from "../../hooks/Currency";

const CustomerReport = (props) => {
  const { _store } = props;

  const { t } = useTranslation();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { setLoader } = useContext(AppContext);

  const [report, setReport] = useState(null);
  const reportRef = useRef(null);

  const [viewData, setViewData] = useState({});
  const [showSaleDetails, setShowSaleDetails] = useState(false);

  const [refundViewData, setRefundViewData] = useState(null);
  const [showRefundDetails, setShowRefundDetails] = useState(false);

  const [paymentViewData, setPaymentViewData] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);

  useEffect(() => {
    if (!customerId) {
      setReport(null);
      return;
    }

    setLoader(true);

    api(
      `${endpoints.CustomerPayment}/Report/${customerId}`
    )
      .get()
      .then((res) => {
        setLoader(false);

        if (res?.data?.status === 200) {
          setReport(res.data.result);
        } else {
          setReport(null);
        }
      })
      .catch((err) => {
        setLoader(false);

        console.error(
          "Customer Report:",
          err
        );

        setReport(null);
      });
  }, [customerId, setLoader]);

  const getTransactionType = (type) => {
    switch (type) {
      case "Invoice":
        return t("invoice");

      case "Refund":
        return t("refund");

      case "Payment":
        return t("payment");

      default:
        return type;
    }
  };

  const getRefundType = (refundType) => {
    switch (Number(refundType)) {
      case 1:
        return t("returnMoney");

      case 2:
        return t("customerCredit");

      case 3:
        return t("reduceBalance");

      default:
        return "";
    }
  };

  const getPayType = (payType) => {
    switch (Number(payType)) {
      case 1:
        return t("cash");

      case 2:
        return t("card");

      case 3:
        return t("stripe");

      case 4:
        return t("check");

      case 5:
        return t("payLater");

      case 6:
        return t("split");

      case 7:
        return t("other");

      default:
        return "";
    }
  };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleString();
  };

  if (!report) {
    return null;
  }

  const customer = report.customer || {};
  const summary = report.summary || {};
  const transactions = report.transactions || [];

  const handleViewInvoice = (sellId) => {
    if (!sellId) return;

    setLoader(true);

    api(`${endpoints.Sell}/GetById/${sellId}`)
        .get()
        .then((res) => {
        setLoader(false);

        if (
            res?.data?.status === 200 &&
            res?.data?.result
        ) {
            setViewData(res.data.result);
            setShowSaleDetails(true);
        }
        })
        .catch((err) => {
        setLoader(false);
        console.error("Load Invoice:", err);
        });
    };

    const handleViewRefund = (transaction) => {
        setRefundViewData(transaction);
        setShowRefundDetails(true);
    };

    const handleViewPayment = (transaction) => {
        setPaymentViewData(transaction);
        setShowPaymentDetails(true);
    };

  return (
    <>
        <Box ref={reportRef} className="customer-report-print">

        {/* Customer Header */}
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                }}
                >
                <Box>
                    <Typography variant="h5">
                    {t("customerReport")}
                    </Typography>

                    <Typography
                    variant="h6"
                    sx={{ mt: 1 }}
                    >
                    {customer.customerName}
                    </Typography>

                    {customer.phone && (
                    <Typography variant="body2">
                        {customer.phone}
                    </Typography>
                    )}

                    {customer.email && (
                    <Typography variant="body2">
                        {customer.email}
                    </Typography>
                    )}
                </Box>

                <Box
                    className="no-print"
                    sx={{
                    display: "flex",
                    gap: 1,
                    }}
                >
                    <ReportPrintButton
                        reportRef={reportRef}
                        paperSize="A4"
                        reportTitle={t("customerReport")}
                    />

                    <Button
                    variant="outlined"
                    onClick={() =>
                        navigate("/customers/customer-balance-report")
                    }
                    >
                    {t("back")}
                    </Button>
                </Box>
                </Box>
            </CardContent>
        </Card>

        {/* Summary */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
            <Card>
                <CardContent>
                <Typography variant="body2">
                    {t("originalPurchases")}
                </Typography>

                <Typography variant="h6">
                    {formatCurrency(
                    summary.totalOriginalPurchases || 0,
                    _store
                    )}
                </Typography>
                </CardContent>
            </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
            <Card>
                <CardContent>
                <Typography variant="body2">
                    {t("totalRefunds")}
                </Typography>

                <Typography variant="h6">
                    {formatCurrency(
                    summary.totalRefunds || 0,
                    _store
                    )}
                </Typography>
                </CardContent>
            </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
            <Card>
                <CardContent>
                <Typography variant="body2">
                    {t("netPurchases")}
                </Typography>

                <Typography variant="h6">
                    {formatCurrency(
                    summary.totalPurchase || 0,
                    _store
                    )}
                </Typography>
                </CardContent>
            </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
            <Card>
                <CardContent>
                <Typography variant="body2">
                    {t("totalPaid")}
                </Typography>

                <Typography variant="h6">
                    {formatCurrency(
                    summary.totalPaid || 0,
                    _store
                    )}
                </Typography>
                </CardContent>
            </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
            <Card>
                <CardContent>
                <Typography variant="body2">
                    {t("currentBalance")}
                </Typography>

                <Typography variant="h6">
                    {formatCurrency(
                    summary.balance || 0,
                    _store
                    )}
                </Typography>
                </CardContent>
            </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
            <Card>
                <CardContent>
                <Typography variant="body2">
                    {t("customerCredit")}
                </Typography>

                <Typography variant="h6">
                    {formatCurrency(
                    summary.credit || 0,
                    _store
                    )}
                </Typography>
                </CardContent>
            </Card>
            </Grid>
        </Grid>

        {/* Transactions */}
        <Card>
            <CardContent>
            <Typography variant="h6" mb={2}>
                {t("transactions")}
            </Typography>

            <TableContainer component={Paper}>
                <Table size="small">
                <TableHead>
                    <TableRow>
                    <TableCell>
                        {t("date")}
                    </TableCell>

                    <TableCell>
                        {t("type")}
                    </TableCell>

                    <TableCell>
                        {t("reference")}
                    </TableCell>

                    <TableCell>
                        {t("method")}
                    </TableCell>

                    <TableCell align="right">
                        {t("amount")}
                    </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {transactions.map(
                    (transaction, index) => (
                        <TableRow
                        key={`${transaction.type}-${transaction.id}-${index}`}
                        >
                        <TableCell>
                            {formatDate(
                            transaction.date
                            )}
                        </TableCell>

                        <TableCell>
                            {getTransactionType(
                            transaction.type
                            )}
                        </TableCell>

                        <TableCell>
                            {/* INVOICE */}
                            {transaction.type === "Invoice" && (
                                <Button
                                variant="text"
                                size="small"
                                sx={{
                                    p: 0,
                                    minWidth: 0,
                                    textTransform: "none",
                                }}
                                onClick={() =>
                                    handleViewInvoice(transaction.sellId)
                                }
                                >
                                Invoice #{transaction.sellId}
                                </Button>
                            )}

                            {/* PAYMENT */}
                            {transaction.type === "Payment" && (
                                <>
                                    <Button
                                    variant="text"
                                    size="small"
                                    sx={{
                                        p: 0,
                                        minWidth: 0,
                                        textTransform: "none",
                                    }}
                                    onClick={() =>
                                        handleViewPayment(transaction)
                                    }
                                    >
                                    Payment #{transaction.id}
                                    </Button>

                                    {transaction.sellId && (
                                    <>
                                        {" / "}

                                        <Button
                                        variant="text"
                                        size="small"
                                        sx={{
                                            p: 0,
                                            minWidth: 0,
                                            textTransform: "none",
                                        }}
                                        onClick={() =>
                                            handleViewInvoice(transaction.sellId)
                                        }
                                        >
                                        Invoice #{transaction.sellId}
                                        </Button>
                                    </>
                                    )}
                                </>
                                )}

                            {/* REFUND */}
                            {transaction.type === "Refund" && (
                                <>
                                    <Button
                                    variant="text"
                                    size="small"
                                    sx={{
                                        p: 0,
                                        minWidth: 0,
                                        textTransform: "none",
                                    }}
                                    onClick={() =>
                                        handleViewRefund(transaction)
                                    }
                                    >
                                    Refund #{transaction.id}
                                    </Button>

                                    {" / "}

                                    <Button
                                    variant="text"
                                    size="small"
                                    sx={{
                                        p: 0,
                                        minWidth: 0,
                                        textTransform: "none",
                                    }}
                                    onClick={() =>
                                        handleViewInvoice(transaction.sellId)
                                    }
                                    >
                                    Invoice #{transaction.sellId}
                                    </Button>
                                </>
                                )}

                            {/* Customer Credit Used */}
                            {Number(transaction.customerCreditUsed) > 0 && (
                                <Typography
                                variant="caption"
                                display="block"
                                >
                                {t("customerCreditUsed")}:{" "}
                                {formatCurrency(
                                    transaction.customerCreditUsed,
                                    _store
                                )}
                                </Typography>
                            )}
                        </TableCell>

                        <TableCell>
                            {transaction.type ===
                            "Refund"
                            ? getRefundType(
                                transaction.refundType
                                )
                            : getPayType(
                                transaction.payType
                                )}
                        </TableCell>

                        <TableCell align="right">
                            {formatCurrency(
                            transaction.amount || 0,
                            _store
                            )}
                        </TableCell>
                        </TableRow>
                    )
                    )}

                    {transactions.length === 0 && (
                    <TableRow>
                        <TableCell
                        colSpan={5}
                        align="center"
                        >
                        {t("noData")}
                        </TableCell>
                    </TableRow>
                    )}
                </TableBody>
                </Table>
            </TableContainer>
            </CardContent>
        </Card>
        </Box>

        <Dialog
            show={showSaleDetails}
            maxWidth="lg"
            onClose={false}
            >
            {viewData?.id && (
                <ViewProd
                setShowForm={setShowSaleDetails}
                viewData={viewData}
                />
            )}
        </Dialog>

    {refundViewData && (
        <Dialog
            show={showRefundDetails}
            onClose={() => setShowRefundDetails(false)}
            maxWidth="md"
        >
            <Box sx={{ p: 3 }}>
            <Typography variant="h5" mb={2}>
                {t("refundDetails")}
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Typography>
                <strong>{t("refundId")}:</strong>{" "}
                {refundViewData.id}
                </Typography>

                <Typography>
                <strong>{t("invoiceId")}:</strong>{" "}
                {refundViewData.sellId}
                </Typography>

                <Typography>
                <strong>{t("date")}:</strong>{" "}
                {refundViewData.date
                    ? new Date(refundViewData.date).toLocaleString()
                    : "-"}
                </Typography>

                <Typography>
                <strong>{t("cashier")}:</strong>{" "}
                {refundViewData.user
                    ? `${refundViewData.user.firstName || ""} ${
                        refundViewData.user.lastName || ""
                    }`.trim()
                    : "-"}
                </Typography>

                <Typography>
                <strong>{t("refundType")}:</strong>{" "}
                {getRefundType(refundViewData.refundType)}
                </Typography>

                <Typography>
                <strong>{t("refundMethod")}:</strong>{" "}
                {Number(refundViewData.refundType) === 1
                    ? getPayType(refundViewData.payType)
                    : "-"}
                </Typography>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                <TableHead>
                    <TableRow>
                    <TableCell>{t("product")}</TableCell>
                    <TableCell align="right">{t("qty")}</TableCell>
                    <TableCell align="right">{t("price")}</TableCell>
                    <TableCell align="right">{t("total")}</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {(refundViewData.items || []).map((item, index) => (
                    <TableRow key={item.id || index}>
                        <TableCell>
                        {item.product?.name ||
                            (Number(item.itemType) === 2
                            ? t("lottery")
                            : "-")}
                        </TableCell>

                        <TableCell align="right">
                        {item.qty}
                        </TableCell>

                        <TableCell align="right">
                        {formatCurrency(
                            Number(item.price || 0),
                            _store
                        )}
                        </TableCell>

                        <TableCell align="right">
                        {formatCurrency(
                            Number(item.qty || 0) *
                            Number(item.price || 0),
                            _store
                        )}
                        </TableCell>
                    </TableRow>
                    ))}
                </TableBody>
                </Table>
            </TableContainer>

            <Box
                sx={{
                mt: 2,
                textAlign: "right",
                fontSize: 18,
                }}
            >
                <div>
                {t("subtotal")}:{" "}
                <strong>
                    {formatCurrency(
                    Number(refundViewData.subtotal || 0),
                    _store
                    )}
                </strong>
                </div>

                <div>
                {t("tax")}:{" "}
                <strong>
                    {formatCurrency(
                    Number(refundViewData.tax || 0),
                    _store
                    )}
                </strong>
                </div>

                <div>
                {t("refundTotal")}:{" "}
                <strong>
                    {formatCurrency(
                    Number(refundViewData.subtotal || 0) +
                        Number(refundViewData.tax || 0),
                    _store
                    )}
                </strong>
                </div>
            </Box>

            <Box
                sx={{
                mt: 3,
                display: "flex",
                justifyContent: "flex-end",
                }}
            >
                <Button
                variant="contained"
                onClick={() => {
                    setShowRefundDetails(false);
                    setRefundViewData(null);
                }}
                >
                {t("close")}
                </Button>
            </Box>
            </Box>
        </Dialog>
        )}

        {paymentViewData && (
        <Dialog
            show={showPaymentDetails}
            onClose={() => setShowPaymentDetails(false)}
            maxWidth="sm"
        >
            <Box sx={{ p: 3 }}>
            <Typography variant="h5" mb={2}>
                {t("paymentDetails")}
            </Typography>

            <Typography sx={{ mb: 1 }}>
                <strong>{t("payment")}:</strong>{" "}
                #{paymentViewData.id}
            </Typography>

            <Typography sx={{ mb: 1 }}>
                <strong>{t("invoice")}:</strong>{" "}
                {paymentViewData.sellId
                ? `#${paymentViewData.sellId}`
                : "-"}
            </Typography>

            <Typography sx={{ mb: 1 }}>
                <strong>{t("date")}:</strong>{" "}
                {paymentViewData.date
                ? new Date(
                    paymentViewData.date
                    ).toLocaleString()
                : "-"}
            </Typography>

            <Typography sx={{ mb: 1 }}>
                <strong>{t("amount")}:</strong>{" "}
                {formatCurrency(
                Number(paymentViewData.amount || 0),
                _store
                )}
            </Typography>

            <Typography sx={{ mb: 1 }}>
                <strong>{t("paymentType")}:</strong>{" "}
                {getPayType(paymentViewData.payType) || "-"}
            </Typography>

            <Typography sx={{ mb: 1 }}>
                <strong>{t("cashier")}:</strong>{" "}
                {paymentViewData.user
                ? `${paymentViewData.user.firstName || ""} ${
                    paymentViewData.user.lastName || ""
                    }`.trim()
                : "-"}
            </Typography>

            <Typography sx={{ mb: 1 }}>
                <strong>{t("notes")}:</strong>{" "}
                {paymentViewData.notes || "-"}
            </Typography>

            <Box
                sx={{
                mt: 3,
                display: "flex",
                justifyContent: "flex-end",
                }}
            >
                <Button
                variant="contained"
                onClick={() => {
                    setShowPaymentDetails(false);
                    setPaymentViewData(null);
                }}
                >
                {t("close")}
                </Button>
            </Box>
            </Box>
        </Dialog>
        )}

    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

export default connect(
  mapStateToProps
)(CustomerReport);